/* global $ */
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from "../axiosInstance";
import Swal from "sweetalert2";
import Footer from '../components/Footer';

const STATUSES = [
    'new',
    'contacted',
    'awaiting_response',
    'follow_up_scheduled',
    'follow_up_done',
    'interested',
    'qualified',
    'negotiation',
    'not_responding',
    'lost',
    'converted'
];

const STATUS_COLORS = {
    new: 'bg-primary',                  // blue
    contacted: 'bg-info',               // light blue
    awaiting_response: 'bg-secondary',  // grey
    follow_up_scheduled: 'bg-warning',  // yellow
    follow_up_done: 'bg-warning text-dark',
    interested: 'bg-info',
    qualified: 'bg-success',            // green
    negotiation: 'bg-dark',             // black
    not_responding: 'bg-secondary',
    lost: 'bg-danger',                  // red
    converted: 'bg-success'
};

const INTERACTION_TYPES = [
    'Call',
    'WhatsApp',
    'Email',
    'SMS',
    'Meeting',
    'Follow-up',
    'Status Change',
    'Note'
];

function LeadDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const chatEndRef = useRef(null);

    const [lead, setLead] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('details');

    // Interactions
    const [interactions, setInteractions] = useState([]);
    const [interactionLoading, setInteractionLoading] = useState(false);
    const [newInteraction, setNewInteraction] = useState({ interaction_type: 'Call', subject: '', notes: '', next_followup_at: '' });
    const [sendingInteraction, setSendingInteraction] = useState(false);

    // Assignment logs
    const [assignLogs, setAssignLogs] = useState([]);
    const [assignLoading, setAssignLoading] = useState(false);

    // Reassign
    const [users, setUsers] = useState([]);
    const [queues, setQueues] = useState([]);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [assignData, setAssignData] = useState({ assigned_user_id: '', lead_queue_id: '', note: '' });
    const [assigning, setAssigning] = useState(false);

    // Quick status update
    const [updatingStatus, setUpdatingStatus] = useState(false);

    // ─── Fetch ────────────────────────────────────────────────────────────────────

    const fetchLead = async () => {
        try {
            setLoading(true);
            const res = await axiosInstance.get(`/api/leads/${id}`);
            setLead(res.data.data || res.data);
        } catch {
            Swal.fire({ icon: 'error', title: 'Error!', text: 'Failed to fetch lead' });
        } finally {
            setLoading(false);
        }
    };

    const fetchInteractions = async () => {
        try {
            setInteractionLoading(true);
            const res = await axiosInstance.get(`/api/leadInteractions?lead_id=${id}`);
            setInteractions(res.data.data || res.data || []);
        } catch { setInteractions([]); } finally { setInteractionLoading(false); }
    };

    const fetchAssignLogs = async () => {
        try {
            setAssignLoading(true);
            const res = await axiosInstance.get(`/api/leadAssign?lead_id=${id}`);
            setAssignLogs(res.data.data || res.data || []);
        } catch { setAssignLogs([]); } finally { setAssignLoading(false); }
    };

    const fetchUsers = async () => {
        try {
            const [uRes, qRes] = await Promise.all([
                axiosInstance.get('/api/users'),
                axiosInstance.get('/api/leadqueue'),
            ]);
            setUsers(uRes.data.data || uRes.data || []);
            setQueues(qRes.data.data || qRes.data || []);
        } catch { }
    };

    useEffect(() => {
        fetchLead();
        fetchInteractions();
        fetchAssignLogs();
        fetchUsers();
    }, [id]);

    useEffect(() => {
        if (activeTab === 'interactions') {
            setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
        }
    }, [activeTab, interactions]);

    // ─── Quick Status Update ──────────────────────────────────────────────────────

    const handleStatusChange = async (newStatus) => {
        setUpdatingStatus(true);
        try {
            await axiosInstance.put(`/api/leads/${id}`, { status: newStatus, updated_by: localStorage.getItem('user_id') });
            setLead(prev => ({ ...prev, status: newStatus }));
            Swal.fire({ icon: 'success', title: 'Status Updated!', timer: 1200, showConfirmButton: false });
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to update status' });
        }
        setUpdatingStatus(false);
    };

    // ─── Add Interaction ──────────────────────────────────────────────────────────

    const sendInteraction = async (e) => {
        e.preventDefault();
        if (!newInteraction.notes.trim()) return;
        setSendingInteraction(true);
        try {
            const user_id = localStorage.getItem('user_id');
            await axiosInstance.post('/api/leadInteractions', {
                lead_id: id,
                user_id,
                interaction_type: newInteraction.interaction_type,
                subject: newInteraction.subject || null,
                notes: newInteraction.notes,
                next_followup_at: newInteraction.next_followup_at || null,
                created_by: user_id,
                updated_by: user_id,
            });
            setNewInteraction({ interaction_type: 'Call', subject: '', notes: '', next_followup_at: '' });
            await fetchInteractions();
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to save interaction' });
        }
        setSendingInteraction(false);
    };

    // ─── Reassign Lead ────────────────────────────────────────────────────────────

    const handleReassign = async () => {
        if (!assignData.assigned_user_id && !assignData.lead_queue_id) {
            Swal.fire({ icon: 'warning', title: 'Select user or queue to assign' }); return;
        }
        setAssigning(true);
        try {
            const user_id = localStorage.getItem('user_id');
            // Update lead
            await axiosInstance.put(`/api/leads/${id}`, {
                assigned_user_id: assignData.assigned_user_id || null,
                lead_queue_id: assignData.lead_queue_id || null,
                assigned_at: new Date().toISOString(),
                updated_by: user_id,
            });
            // Log assignment
            if (assignData.assigned_user_id) {
                await axiosInstance.post('/api/leadAssign', {
                    lead_id: id,
                    assigned_to: assignData.assigned_user_id,
                    assigned_by: user_id,
                    assigned_at: new Date().toISOString(),
                    note: assignData.note || null,
                });
            }
            await fetchLead();
            await fetchAssignLogs();
            setShowAssignModal(false);
            setAssignData({ assigned_user_id: '', lead_queue_id: '', note: '' });
            Swal.fire({ icon: 'success', title: 'Lead Assigned!', timer: 1500, showConfirmButton: false });
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to assign lead' });
        }
        setAssigning(false);
    };

    // ─── Convert to Customer ─────────────────────────────────────────────────────

    const handleConvert = async () => {
        const result = await Swal.fire({
            title: 'Convert to Customer?',
            html: `<p>This will create a customer record from <strong>${lead.name}</strong> and mark the lead as converted.</p>`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#28a745',
            confirmButtonText: 'Yes, Convert!'
        });
        if (!result.isConfirmed) return;

        try {
            const user_id = localStorage.getItem('user_id');
            // Create customer from lead data
            await axiosInstance.post('/api/customer', {
                lead_id: id,
                name: lead.name,
                email: lead.email || null,
                phone: lead.phone,
                alternate_phone: lead.alternate_phone || null,
                country_id: lead.country_id || null,
                state_id: lead.state_id || null,
                city_id: lead.city_id || null,
                status: 'active',
                isactive: true,
                created_by: user_id,
                updated_by: user_id,
            });
            // Mark lead as converted
            await axiosInstance.put(`/api/leads/${id}`, {
                status: 'converted',
                converted_at: new Date().toISOString(),
                updated_by: user_id,
            });
            await fetchLead();
            Swal.fire({ icon: 'success', title: 'Converted!', text: 'Lead has been converted to a customer.', timer: 2000, showConfirmButton: false });
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to convert lead' });
        }
    };

    // ─── Delete ───────────────────────────────────────────────────────────────────

    const handleDelete = async () => {
        const result = await Swal.fire({
            title: 'Delete Lead?', text: "This action cannot be undone.", icon: 'warning',
            showCancelButton: true, confirmButtonColor: '#d33', confirmButtonText: 'Yes, delete!'
        });
        if (result.isConfirmed) {
            try {
                await axiosInstance.delete(`/api/leads/${id}`);
                Swal.fire({ icon: 'success', title: 'Deleted!', timer: 1500, showConfirmButton: false });
                navigate('/lead');
            } catch {
                Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to delete lead' });
            }
        }
    };

    // ─── Helpers ─────────────────────────────────────────────────────────────────

    const InfoRow = ({ label, value }) => (
        <div className="col-md-6 mb-3">
            <label className="text-muted small mb-1 d-block">{label}</label>
            <span className="fw-medium">{value || <span className="text-muted">—</span>}</span>
        </div>
    );

    const formatDate = (d) => d ? new Date(d).toLocaleString() : '—';
    const formatDateOnly = (d) => d ? new Date(d).toLocaleDateString() : '—';

    const INTERACTION_ICONS = {
        Call: 'fa-phone', WhatsApp: 'fa-whatsapp', Email: 'fa-envelope',
        Meeting: 'fa-users', Note: 'fa-sticky-note', 'Follow-up': 'fa-clock',
    };
    const INTERACTION_COLORS = {
        Call: '#28a745', WhatsApp: '#25D366', Email: '#007bff',
        Meeting: '#6f42c1', Note: '#fd7e14', 'Follow-up': '#20c997',
    };

    if (loading) return (
        <div className="page-wrapper">
            <div className="content container-fluid d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
            </div>
            <Footer />
        </div>
    );

    if (!lead) return (
        <div className="page-wrapper">
            <div className="content container-fluid"><div className="alert alert-danger">Lead not found.</div></div>
            <Footer />
        </div>
    );

    return (
        <div>
            <div className="page-wrapper">
                <div className="content container-fluid">

                    {/* ── Header ── */}
                    <div className="page-header">
                        <div className="content-page-header d-flex align-items-center justify-content-between flex-wrap gap-2">
                            <div className="d-flex align-items-center">
                                <button className="btn btn-sm btn-outline-secondary me-3" onClick={() => navigate(-1)}>
                                    <i className="fa fa-arrow-left me-1"></i> Back
                                </button>
                                <div>
                                    <h5 className="mb-0">{lead.name || `Lead-${lead.phone}`}</h5>
                                    <small className="text-muted">
                                        <i className="fa fa-phone me-1"></i>{lead.phone}
                                        {lead.source && <> · {lead.source}</>}
                                    </small>
                                </div>
                                <span className={`badge ms-3 ${STATUS_COLORS[lead.status] || 'bg-secondary'}`}>
                                    {lead.status?.replace('_', ' ') || 'N/A'}
                                </span>
                            </div>
                            <div className="d-flex gap-2 flex-wrap">
                                {/* Quick status change */}
                                <select className="form-select form-select-sm" style={{ width: 'auto' }}
                                    value={lead.status} disabled={updatingStatus}
                                    onChange={e => handleStatusChange(e.target.value)}>
                                    {STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                                </select>
                                <button className="btn btn-info btn-sm text-white" onClick={() => setShowAssignModal(true)}>
                                    <i className="fa fa-user-plus me-1"></i> Assign
                                </button>
                                {lead.status !== 'converted' && (
                                    <button className="btn btn-success btn-sm" onClick={handleConvert}>
                                        <i className="fa fa-check-circle me-1"></i> Convert
                                    </button>
                                )}
                                <button className="btn btn-primary btn-sm"
                                    onClick={() => navigate('/add-lead', { state: { lead, isEdit: true } })}>
                                    <i className="fa fa-edit me-1"></i> Edit
                                </button>
                                <button className="btn btn-danger btn-sm" onClick={handleDelete}>
                                    <i className="fa fa-trash-alt me-1"></i> Delete
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Breadcrumb */}
                    <nav aria-label="breadcrumb" className="mb-3">
                        <ol className="breadcrumb">
                            <li className="breadcrumb-item">
                                <span style={{ cursor: 'pointer', color: '#007bff' }} onClick={() => navigate('/lead')}>Leads</span>
                            </li>
                            <li className="breadcrumb-item active">{lead.name || `Lead-${lead.phone}`}</li>
                        </ol>
                    </nav>

                    {/* ── Tabs ── */}
                    <ul className="nav nav-tabs mb-3">
                        {[
                            { key: 'details', icon: 'fa-info-circle', label: 'Details' },
                            { key: 'interactions', icon: 'fa-comments', label: `Interactions (${interactions.length})` },
                            { key: 'assignment', icon: 'fa-history', label: `Assignment Logs (${assignLogs.length})` },
                        ].map(t => (
                            <li className="nav-item" key={t.key}>
                                <button className={`nav-link ${activeTab === t.key ? 'active' : ''}`}
                                    onClick={() => setActiveTab(t.key)}>
                                    <i className={`fa ${t.icon} me-2`}></i>{t.label}
                                </button>
                            </li>
                        ))}
                    </ul>

                    {/* ── Tab: Details ── */}
                    {activeTab === 'details' && (
                        <div className="card mb-0">
                            <div className="card-body">
                                <h6 className="text-primary border-bottom pb-2 mb-3">Contact Information</h6>
                                <div className="row">
                                    <InfoRow label="Name" value={lead.name} />
                                    <InfoRow label="Phone" value={<a href={`tel:${lead.phone}`}>{lead.phone}</a>} />
                                    <InfoRow label="Alternate Phone" value={lead.alternate_phone ? <a href={`tel:${lead.alternate_phone}`}>{lead.alternate_phone}</a> : null} />
                                    <InfoRow label="Email" value={lead.email ? <a href={`mailto:${lead.email}`} className="text-primary">{lead.email}</a> : null} />
                                    <InfoRow label="Source" value={lead.source} />
                                    <InfoRow label="Status" value={
                                        <span className={`badge ${STATUS_COLORS[lead.status] || 'bg-secondary'}`}>
                                            {lead.status?.replace('_', ' ')}
                                        </span>
                                    } />
                                </div>

                                <h6 className="text-primary border-bottom pb-2 mb-3 mt-4">Travel Details</h6>
                                <div className="row">
                                    <InfoRow label="Travel Type" value={lead.travel_type} />
                                    <InfoRow label="Preferred Start Date" value={formatDateOnly(lead.preferred_start_date)} />
                                    <InfoRow label="No. of People" value={lead.no_of_people} />
                                </div>

                                <h6 className="text-primary border-bottom pb-2 mb-3 mt-4">Location</h6>
                                <div className="row">
                                    <InfoRow label="Country" value={lead.country?.name} />
                                    <InfoRow label="State" value={lead.state?.name} />
                                    <InfoRow label="City" value={lead.city?.name} />
                                </div>

                                <h6 className="text-primary border-bottom pb-2 mb-3 mt-4">Assignment</h6>
                                <div className="row">
                                    <InfoRow label="Lead Queue" value={lead.lead_queue?.name} />
                                    <InfoRow label="Assigned To" value={lead.assigned_id?.user_name} />
                                    <InfoRow label="Assigned At" value={formatDate(lead.assigned_at)} />
                                    <InfoRow label="Converted At" value={formatDate(lead.converted_at)} />
                                </div>

                                {lead.notes && (
                                    <>
                                        <h6 className="text-primary border-bottom pb-2 mb-3 mt-4">Notes</h6>
                                        <p className="text-muted" style={{ whiteSpace: 'pre-wrap' }}>{lead.notes}</p>
                                    </>
                                )}

                                <h6 className="text-primary border-bottom pb-2 mb-3 mt-4">Record Info</h6>
                                <div className="row">
                                    <InfoRow label="Created At" value={formatDate(lead.createdAt)} />
                                    <InfoRow label="Updated At" value={formatDate(lead.updatedAt)} />
                                    <InfoRow label="Created By" value={lead.createdBy?.user_name} />
                                    <InfoRow label="Updated By" value={lead.updatedBy?.user_name} />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Tab: Interactions (Chat Style) ── */}
                    {activeTab === 'interactions' && (
                        <div className="card mb-0">
                            <div className="card-body p-0">
                                {/* Chat window */}
                                <div style={{ height: '420px', overflowY: 'auto', padding: '20px', background: '#f8f9fa' }}>
                                    {interactionLoading ? (
                                        <div className="text-center py-4">
                                            <div className="spinner-border text-primary" role="status"></div>
                                        </div>
                                    ) : interactions.length === 0 ? (
                                        <div className="text-center text-muted py-5">
                                            <i className="fa fa-comments fa-3x mb-3 d-block"></i>
                                            No interactions yet. Add the first one below.
                                        </div>
                                    ) : (
                                        interactions.map((item, idx) => {
                                            const isMe = item.created_by === localStorage.getItem('user_id');
                                            return (
                                                <div key={item.id || idx}
                                                    className={`d-flex mb-3 ${isMe ? 'justify-content-end' : 'justify-content-start'}`}>
                                                    <div style={{
                                                        maxWidth: '70%',
                                                        background: isMe ? '#007bff' : '#fff',
                                                        color: isMe ? '#fff' : '#333',
                                                        borderRadius: isMe ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                                                        padding: '10px 14px',
                                                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                                    }}>
                                                        {/* Type badge */}
                                                        <div className="mb-1">
                                                            <span style={{
                                                                fontSize: '11px',
                                                                background: isMe ? 'rgba(255,255,255,0.2)' : INTERACTION_COLORS[item.interaction_type] || '#6c757d',
                                                                color: isMe ? '#fff' : '#fff',
                                                                borderRadius: '10px',
                                                                padding: '2px 8px',
                                                            }}>
                                                                <i className={`fab fa-fw ${INTERACTION_ICONS[item.interaction_type] || 'fa-comment'} me-1`}></i>
                                                                {item.interaction_type}
                                                            </span>
                                                        </div>
                                                        {item.subject && (
                                                            <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '4px' }}>{item.subject}</div>
                                                        )}
                                                        <div style={{ fontSize: '14px', whiteSpace: 'pre-wrap' }}>{item.notes}</div>
                                                        {item.next_followup_at && (
                                                            <div style={{ fontSize: '11px', marginTop: '6px', opacity: 0.8 }}>
                                                                <i className="fa fa-clock me-1"></i>
                                                                Follow-up: {new Date(item.next_followup_at).toLocaleDateString()}
                                                            </div>
                                                        )}
                                                        <div style={{ fontSize: '11px', marginTop: '4px', opacity: 0.7, textAlign: 'right' }}>
                                                            {item.users?.user_name || 'Unknown'} · {formatDate(item.createdAt)}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                    <div ref={chatEndRef}></div>
                                </div>

                                {/* Input area */}
                                <div style={{ borderTop: '1px solid #dee2e6', padding: '16px', background: '#fff' }}>
                                    <form onSubmit={sendInteraction}>
                                        <div className="row g-2 mb-2">
                                            <div className="col-md-3">
                                                <select className="form-control form-control-sm"
                                                    value={newInteraction.interaction_type}
                                                    onChange={e => setNewInteraction(p => ({ ...p, interaction_type: e.target.value }))}>
                                                    {INTERACTION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                                </select>
                                            </div>
                                            <div className="col-md-4">
                                                <input type="text" className="form-control form-control-sm"
                                                    placeholder="Subject (optional)"
                                                    value={newInteraction.subject}
                                                    onChange={e => setNewInteraction(p => ({ ...p, subject: e.target.value }))} />
                                            </div>
                                            <div className="col-md-5">
                                                <input type="datetime-local" className="form-control form-control-sm"
                                                    title="Next Follow-up Date"
                                                    value={newInteraction.next_followup_at}
                                                    onChange={e => setNewInteraction(p => ({ ...p, next_followup_at: e.target.value }))} />
                                            </div>
                                        </div>
                                        <div className="d-flex gap-2">
                                            <textarea className="form-control" rows="2"
                                                placeholder="Type your note here... (e.g. Called customer, interested in Bali package)"
                                                value={newInteraction.notes}
                                                onChange={e => setNewInteraction(p => ({ ...p, notes: e.target.value }))}
                                                onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) sendInteraction(e); }}
                                            ></textarea>
                                            <button type="submit" className="btn btn-primary px-4" disabled={sendingInteraction || !newInteraction.notes.trim()}>
                                                {sendingInteraction
                                                    ? <span className="spinner-border spinner-border-sm"></span>
                                                    : <i className="fa fa-paper-plane"></i>}
                                            </button>
                                        </div>
                                        <small className="text-muted">Ctrl+Enter to send</small>
                                    </form>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Tab: Assignment Logs ── */}
                    {activeTab === 'assignment' && (
                        <div className="card mb-0">
                            <div className="card-body">
                                {assignLoading ? (
                                    <div className="text-center py-4">
                                        <div className="spinner-border text-primary" role="status"></div>
                                    </div>
                                ) : assignLogs.length === 0 ? (
                                    <div className="text-center text-muted py-5">
                                        <i className="fa fa-history fa-3x mb-3 d-block"></i>
                                        No assignment history yet.
                                    </div>
                                ) : (
                                    <div className="timeline" style={{ position: 'relative', paddingLeft: '30px' }}>
                                        <div style={{ position: 'absolute', left: '10px', top: 0, bottom: 0, width: '2px', background: '#dee2e6' }}></div>
                                        {assignLogs.map((log, idx) => (
                                            <div key={log.id || idx} style={{ position: 'relative', marginBottom: '24px' }}>
                                                <div style={{
                                                    position: 'absolute', left: '-24px', top: '4px',
                                                    width: '12px', height: '12px', borderRadius: '50%',
                                                    background: '#007bff', border: '2px solid #fff', boxShadow: '0 0 0 2px #007bff'
                                                }}></div>
                                                <div className="card border-0 shadow-sm">
                                                    <div className="card-body py-2 px-3">
                                                        <div className="d-flex justify-content-between align-items-start">
                                                            <div>
                                                                <span className="fw-semibold">
                                                                    <i className="fa fa-user me-1 text-primary"></i>
                                                                    {log.users_assigned?.user_name || 'Unknown'}
                                                                </span>
                                                                <span className="text-muted mx-2">assigned by</span>
                                                                <span className="fw-semibold">{log.user_assigned_by?.user_name || 'Unknown'}</span>
                                                            </div>
                                                            <small className="text-muted">{formatDate(log.assigned_at || log.createdAt)}</small>
                                                        </div>
                                                        {log.note && <p className="mb-0 mt-1 text-muted small" style={{ whiteSpace: 'pre-wrap' }}>{log.note}</p>}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                </div>
                <Footer />
            </div>

            {/* ── Assign Modal ── */}
            {showAssignModal && (
                <>
                    <div className="modal fade show" style={{ display: 'block', zIndex: 1050 }}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title"><i className="fa fa-user-plus me-2"></i>Assign Lead</h5>
                                    <button type="button" className="btn-close" onClick={() => setShowAssignModal(false)}></button>
                                </div>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label">Assign To User</label>
                                        <select className="form-control" value={assignData.assigned_user_id}
                                            onChange={e => setAssignData(p => ({ ...p, assigned_user_id: e.target.value }))}>
                                            <option value="">Select User</option>
                                            {users.map(u => <option key={u.id} value={u.id}>{u.user_name}</option>)}
                                        </select>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Move to Queue</label>
                                        <select className="form-control" value={assignData.lead_queue_id}
                                            onChange={e => setAssignData(p => ({ ...p, lead_queue_id: e.target.value }))}>
                                            <option value="">Keep Current Queue</option>
                                            {queues.map(q => <option key={q.id} value={q.id}>{q.name}</option>)}
                                        </select>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Note <small className="text-muted">(optional)</small></label>
                                        <textarea className="form-control" rows="2"
                                            placeholder="Reason for assignment..."
                                            value={assignData.note}
                                            onChange={e => setAssignData(p => ({ ...p, note: e.target.value }))}
                                        ></textarea>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button className="btn btn-secondary" onClick={() => setShowAssignModal(false)}>Cancel</button>
                                    <button className="btn btn-primary" onClick={handleReassign} disabled={assigning}>
                                        {assigning ? <><span className="spinner-border spinner-border-sm me-2"></span>Assigning...</> : 'Assign Lead'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-backdrop fade show" style={{ zIndex: 1040 }}></div>
                </>
            )}
        </div>
    );
}

export default LeadDetails;